import {useQuery} from "@apollo/client";
import {format} from "date-fns";
import {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useCookies} from "react-cookie";
import {Helmet} from "react-helmet-async";
import {useParams, useSearchParams} from "react-router";

import {useTabs} from "@/lib/hooks/useTabs.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {
  GQL_PRIVATE_STATUSPAGE_HISTORY_QUERY,
  GQL_PRIVATE_STATUSPAGE_QUERY,
  GQL_PUBLIC_STATUSPAGE_HISTORY_QUERY,
  GQL_PUBLIC_STATUSPAGE_QUERY,
} from "@/lib/queries/statusPageConsumption.ts";
import type {ComponentStatus} from "@/lib/types/statuspages.ts";
import {
  applyPreviewOverrides,
  isLivePreviewMessage,
  LIVE_PREVIEW_OVERRIDE,
  LIVE_PREVIEW_READY,
  LivePreviewReadyMessage,
  StatusPagePreviewOverrides,
} from "@/pages/statuspages/shared/livePreview.ts";

import {GoogleAnalytics} from "./components/GoogleAnalytics.tsx";
import {ConsumptionNotFound} from "./ConsumptionNotFound.tsx";
import {ConsumptionPageLoader} from "./ConsumptionPageLoader.tsx";
import {ThemeRenderer} from "./themes/ThemeRenderer.tsx";
import {
  CurrentStatusTabType,
  HistoryTabType,
  StatusPageTab,
  TabParams,
  type PublicStatusPage,
  type PublicStatusPageHistoryResult,
} from "./types.ts";
import {DATE_RANGE_END_PARAM, DATE_RANGE_START_PARAM, defaultDateRangeForDays} from "./utils/dateRange.ts";

// useTabs rebuilds the query string from a whitelist on every tab click, so the shared date range
// has to be named there or switching tabs would silently drop it.
const SHARED_DATE_PARAMS = [DATE_RANGE_START_PARAM, DATE_RANGE_END_PARAM];

const mapComponentStatuses = (metadata: PublicStatusPage["component_status_metadata"]): ComponentStatus[] => {
  return (metadata ?? []).map((m) => ({
    value: m.value as ComponentStatus["value"],
    rank: m.rank,
    display: m.display,
    color: m.color,
    is_operational: m.is_operational,
    is_failure: !m.is_operational,
  }));
};

const THEME_COOKIE_NAME = "uptime_sp_theme_settings";
const THEME_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60; // 90 days

const ACCESS_DENIED_MESSAGE = "Access denied.";

const isAccessDeniedError = (
  error: {graphQLErrors?: ReadonlyArray<{message: string}>; message?: string} | undefined | null
): boolean => {
  if (!error) return false;
  if (error.message === ACCESS_DENIED_MESSAGE) return true;
  return error.graphQLErrors?.some((e) => e.message === ACCESS_DENIED_MESSAGE) ?? false;
};

interface StatusPageConsumptionProps {
  mode?: "public" | "private";
  // Set by the Host* wrappers when reached via a custom domain: keep URLs clean and
  // redirects on-domain. Independent of whether the host resolved (so behavior is
  // consistent even on a custom-domain not-found).
  hostMode?: boolean;
  // Host-resolved slug (custom domain, no slug in the path); overrides the URL param.
  slug?: string;
}

const StatusPageConsumption = ({mode = "public", hostMode = false, slug: slugOverride}: StatusPageConsumptionProps) => {
  const params = useParams<{slug?: string; id?: string}>();
  const slug = slugOverride ?? params.slug;
  const id = params.id;
  const [cookies, setCookie] = useCookies([THEME_COOKIE_NAME]);
  const [searchParams, setSearchParams] = useSearchParams();
  // A link carrying a range is a link to the history the visitor was looking at. Legacy opens those
  // on the History tab too, so old shared links land where they did before.
  const sharesDateRange = searchParams.has(DATE_RANGE_START_PARAM) || searchParams.has(DATE_RANGE_END_PARAM);
  const {tab: activeTab, changeTab: changeOuterTab} = useTabs({
    defaultTab: sharesDateRange ? StatusPageTab.HISTORY : StatusPageTab.STATUS,
    urlTabParam: TabParams.TAB,
    tabList: [StatusPageTab.STATUS, StatusPageTab.HISTORY],
    preserveSearchParams: [TabParams.HISTORY, TabParams.STATUS, ...SHARED_DATE_PARAMS],
  });
  const {tab: currentStatusTab, changeTab: changeStatusTab} = useTabs({
    defaultTab: CurrentStatusTabType.OVERVIEW,
    urlTabParam: TabParams.STATUS,
    tabList: Object.values(CurrentStatusTabType),
    preserveSearchParams: [TabParams.TAB, TabParams.HISTORY, ...SHARED_DATE_PARAMS],
  });
  const {tab: historyTab, changeTab: changeHistoryTab} = useTabs({
    defaultTab: HistoryTabType.HISTORY_OVERVIEW,
    urlTabParam: TabParams.HISTORY,
    tabList: Object.values(HistoryTabType),
    preserveSearchParams: [TabParams.TAB, TabParams.STATUS, ...SHARED_DATE_PARAMS],
  });
  const [isDark, setIsDark] = useState(false);
  // Live preview overrides received from the edit form when embedded as a private preview iframe.
  const [livePreviewOverrides, setLivePreviewOverrides] = useState<StatusPagePreviewOverrides | null>(null);
  // Mount HistoryOverview immediately when deep-linking to history; otherwise defer
  // to avoid blocking the initial CurrentStatusOverview render with extra queries.
  const [historyMounted, setHistoryMounted] = useState(
    () => sharesDateRange || searchParams.get(TabParams.TAB) === StatusPageTab.HISTORY
  );

  const isPrivate = mode === "private";
  const parsedId = id ? parseInt(id, 10) : undefined;

  const {
    data: publicData,
    loading: publicLoading,
    error: publicError,
    refetch: publicRefetch,
  } = useQuery<{
    publicStatusPage: PublicStatusPage | null;
  }>(GQL_PUBLIC_STATUSPAGE_QUERY, {
    variables: {slug: slug ?? ""},
    skip: isPrivate || !slug,
    context: {statusPageSlug: slug, skipAuthError: true},
    fetchPolicy: "cache-and-network",
  });

  const {
    data: privateData,
    loading: privateLoading,
    error: privateError,
    refetch: privateRefetch,
  } = useQuery<{
    privateStatusPage: PublicStatusPage | null;
  }>(GQL_PRIVATE_STATUSPAGE_QUERY, {
    variables: {id: parsedId ?? 0},
    skip: !isPrivate || !parsedId,
  });

  const loading = isPrivate ? privateLoading : publicLoading;
  const error = isPrivate ? privateError : publicError;
  const statusPage = isPrivate ? (privateData?.privateStatusPage ?? null) : (publicData?.publicStatusPage ?? null);
  const refetch = isPrivate ? privateRefetch : publicRefetch;

  const pageId = statusPage?.id;

  // Resolved with the same helper as the History tab and the drilldown: N whole days in the page's
  // timezone ending at end-of-day today. Subtracting N days from end-of-day instead started the
  // window on the last instant of the day before, so a page configured for the same N drew one more
  // leading day here than the History tab did, on boundaries offset by the viewer's UTC offset.
  const statusDateRange = useMemo(() => {
    if (!statusPage) return null;
    const {from, to} = defaultDateRangeForDays(statusPage.default_status_date_range || 7, statusPage.used_timezone);
    return {
      start: from,
      end: to,
      startStr: format(from, "yyyyMMdd"),
      endStr: format(to, "yyyyMMdd"),
    };
  }, [statusPage]);

  // The union of both tabs' default windows: this result feeds the Current Status uptime bar and its
  // tooltips, and decides whether the History tab has any events at all, so it has to cover
  // whichever of the two reaches further back. The "operational for" header duration no longer
  // depends on it — the backend resolves that unbounded by the visible range.
  const historyDateRange = useMemo(() => {
    if (!statusPage) return null;
    const days = Math.max(statusPage.default_status_date_range || 7, statusPage.default_history_date_range || 7);
    const {from, to} = defaultDateRangeForDays(days, statusPage.used_timezone);
    return {
      startStr: format(from, "yyyyMMdd"),
      endStr: format(to, "yyyyMMdd"),
    };
  }, [statusPage]);

  const {data: historyData, refetch: historyRefetch} = useQuery<{
    publicStatusPageHistory: PublicStatusPageHistoryResult | null;
  }>(GQL_PUBLIC_STATUSPAGE_HISTORY_QUERY, {
    variables: {
      slug: slug ?? "",
      startDate: historyDateRange?.startStr ?? "",
      endDate: historyDateRange?.endStr ?? "",
    },
    skip: isPrivate || !slug || !historyDateRange,
    context: {statusPageSlug: slug, skipAuthError: true},
    fetchPolicy: "cache-and-network",
  });

  const {data: privateHistoryData, refetch: privateHistoryRefetch} = useQuery<{
    privateStatusPageHistory: PublicStatusPageHistoryResult | null;
  }>(GQL_PRIVATE_STATUSPAGE_HISTORY_QUERY, {
    variables: {
      id: parsedId ?? 0,
      startDate: historyDateRange?.startStr ?? "",
      endDate: historyDateRange?.endStr ?? "",
    },
    skip: !isPrivate || !parsedId || !historyDateRange,
  });

  const historyResult = isPrivate
    ? (privateHistoryData?.privateStatusPageHistory ?? null)
    : (historyData?.publicStatusPageHistory ?? null);

  // Live-preview overrides from the edit form are layered onto the saved page before anything is
  // derived, so tab-visibility, empty-tab collapse, and the rendered themes all reflect them.
  const previewStatusPage = useMemo(
    () => (statusPage ? applyPreviewOverrides(statusPage, livePreviewOverrides) : null),
    [statusPage, livePreviewOverrides]
  );

  // Empty event sub-tabs are hidden individually inside each tab body (driven by
  // hide_empty_tabs_status / hide_empty_tabs_history). The Current Status tab always keeps its
  // Overview, so it never collapses to nothing. The History tab has no such always-on sub-tab, so
  // when hide_empty_tabs_history removes every event sub-tab and the tab has no other content
  // (no Component History, summary metrics, or uptime bar — the latter two render regardless of
  // events), hide the whole History tab rather than show an empty one.
  const historyTabIsEmpty =
    !!previewStatusPage?.hide_empty_tabs_history &&
    !previewStatusPage.show_component_history &&
    !previewStatusPage.show_summary_metrics &&
    !previewStatusPage.show_history_snake &&
    historyResult != null &&
    historyResult.past_incidents.length === 0;

  const effectiveStatusPage = useMemo(() => {
    if (!previewStatusPage) return null;
    if (!historyTabIsEmpty) return previewStatusPage;
    return {...previewStatusPage, show_history_tab: false};
  }, [previewStatusPage, historyTabIsEmpty]);

  // Tab-visibility flags reflect live-preview overrides and the empty-tab collapse (not just saved
  // values) so the edit-form preview moves the user off a tab the moment it is toggled off.
  const showStatusTab = effectiveStatusPage?.show_status_tab;
  const showHistoryTab = effectiveStatusPage?.show_history_tab;

  // Merge active incidents with past incidents (deduplicate by ID)
  const allIncidents = useMemo(() => {
    if (!statusPage) return [];
    const incidentMap = new Map<number, (typeof statusPage.active_incidents)[number]>();
    for (const inc of statusPage.active_incidents) {
      incidentMap.set(inc.id, inc);
    }
    if (historyResult) {
      for (const inc of historyResult.past_incidents) {
        if (!incidentMap.has(inc.id)) {
          incidentMap.set(inc.id, inc);
        }
      }
    }
    return Array.from(incidentMap.values());
  }, [statusPage, historyResult]);

  // Fall back to the other tab when the active one is hidden (saved config or a live-preview toggle)
  useEffect(() => {
    if (pageId != null) {
      const showStatus = showStatusTab !== false;
      const showHistory = !!showHistoryTab;
      if (!showStatus && showHistory) {
        setSearchParams(
          (prev) => {
            if (prev.get(TabParams.TAB) === StatusPageTab.HISTORY) return prev;
            const next = new URLSearchParams(prev);
            next.set(TabParams.TAB, StatusPageTab.HISTORY);
            return next;
          },
          {preventScrollReset: true}
        );
      } else if (!showHistory && showStatus) {
        setSearchParams(
          (prev) => {
            if (prev.get(TabParams.TAB) !== StatusPageTab.HISTORY) return prev;
            const next = new URLSearchParams(prev);
            next.set(TabParams.TAB, StatusPageTab.STATUS);
            return next;
          },
          {preventScrollReset: true}
        );
      }
    }
  }, [pageId, showStatusTab, showHistoryTab, setSearchParams]);

  // Initialize theme from cookie once we have the page ID. useLayoutEffect (not useEffect) so the
  // theme is applied before paint — otherwise re-mounting the page (e.g. back from the drilldown)
  // flashes one light frame before the cookie value takes effect.
  useLayoutEffect(() => {
    if (pageId != null) {
      const settings = cookies[THEME_COOKIE_NAME];
      const saved = settings?.[pageId] === "dark" ? "dark" : "light";
      setIsDark(saved === "dark");
    }
  }, [pageId, cookies]);

  const handleToggleDarkMode = useCallback(() => {
    if (!statusPage) return;
    const newTheme = isDark ? "light" : "dark";
    setIsDark(!isDark);
    const settings = cookies[THEME_COOKIE_NAME] ?? {};
    setCookie(
      THEME_COOKIE_NAME,
      {...settings, [statusPage.id]: newTheme},
      {path: "/", maxAge: THEME_COOKIE_MAX_AGE_SECONDS}
    );
  }, [isDark, statusPage, cookies, setCookie]);

  const handleRefresh = useCallback(() => {
    refetch();
    if (historyDateRange) {
      if (isPrivate && parsedId) {
        privateHistoryRefetch();
      } else if (slug) {
        historyRefetch();
      }
    }
  }, [refetch, historyRefetch, privateHistoryRefetch, slug, parsedId, isPrivate, historyDateRange]);

  // Component names render as real links so they can be opened in a new tab, which means handing
  // the target down rather than a click handler. It stays relative to the current route: these
  // pages are mounted at the SPA root on status-page domains but under `/status-page` in the main
  // app, so a root-absolute path escapes the status page on one of the two mounts. The current
  // query string rides along so the drilldown opens on the range the visitor is already looking at
  // — legacy does the same — and the dark theme is added on top because here it comes from a
  // cookie rather than from the URL the link starts on.
  const componentLinkTo = useCallback(
    (serviceId: number) => {
      const search = new URLSearchParams(searchParams);
      if (isDark) search.set("theme", "dark");
      const query = search.toString();
      // Host mode renders at the clean root, so the link needs the "c/" prefix to reach
      // "/c/{serviceId}"; path mode stays relative to "/{slug}".
      const path = hostMode ? `c/${serviceId}` : `${serviceId}`;
      return query ? `${path}?${query}` : path;
    },
    [hostMode, isDark, searchParams]
  );

  // Stable identity so CurrentStatusOverview's derived lookups (and the memoized component rows)
  // don't rebuild on every render (auto-refresh, dark-mode toggle, cache-and-network revalidation).
  // Depend on the metadata array itself, not the whole page object: an unrelated update (e.g.
  // refreshed incident data) changes effectiveStatusPage but not this array, so the derived
  // lookups — and therefore the memoized ComponentItem rows — keep stable identities.
  const componentStatusMetadata = effectiveStatusPage?.component_status_metadata;
  const componentStatuses = useMemo(
    () => (componentStatusMetadata ? mapComponentStatuses(componentStatusMetadata) : []),
    [componentStatusMetadata]
  );

  const autoRefreshPageKey = isPrivate
    ? parsedId != null
      ? `private:${parsedId}`
      : undefined
    : slug
      ? `public:${slug}`
      : undefined;

  // Mount HistoryOverview after the initial paint so its queries load in the background
  // without interfering with the initial render of CurrentStatusOverview.
  useEffect(() => {
    setHistoryMounted(true);
  }, []);

  // Live preview bridge: only active for a private page embedded inside the edit form iframe.
  useEffect(() => {
    if (!isPrivate) return;
    if (typeof window === "undefined" || window.parent === window) return;

    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // Only trust the embedding edit form, not any same-origin window.
      if (event.source !== window.parent) return;
      if (!isLivePreviewMessage(event.data)) return;
      if (event.data.type === LIVE_PREVIEW_OVERRIDE) {
        setLivePreviewOverrides(event.data.overrides);
      }
    };
    window.addEventListener("message", handler);

    const readyMessage: LivePreviewReadyMessage = {type: LIVE_PREVIEW_READY};
    window.parent.postMessage(readyMessage, window.location.origin);

    return () => window.removeEventListener("message", handler);
  }, [isPrivate]);

  // Redirect to login when access is denied for external-user pages
  useEffect(() => {
    if (!loading && isAccessDeniedError(error) && slug) {
      // Custom domain: keep the visitor on their domain (the slug stays hidden). The edge
      // resolves the host to the page for the login flow. Otherwise use the app host.
      window.location.href = hostMode
        ? `${window.location.origin}/login`
        : `${URL_ORIGINAL_WEBSITE}/statuspage/${encodeURIComponent(slug)}/login`;
    }
  }, [loading, error, slug, hostMode]);

  // Only block on the initial load. Under the default cache-and-network policy a re-mount
  // (e.g. navigating back from the drilldown) reports loading=true while it revalidates even
  // though cached data is already available — gating on !statusPage renders that cached content
  // immediately instead of flashing the full-screen loader.
  if (loading && !statusPage) {
    return <ConsumptionPageLoader />;
  }

  if (error || !effectiveStatusPage) {
    if (isAccessDeniedError(error)) {
      return null;
    }

    return <ConsumptionNotFound />;
  }

  // Private pages must never be indexed. Public pages honor the account setting.
  const noIndex = isPrivate || !effectiveStatusPage.allow_search_indexing;

  return (
    <>
      {noIndex && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      {/* Custom-domain (host-resolved) pages are canonically the domain root; path-based
          pages are self-canonical. Avoids indexing the same page under "/" and "/{slug}". */}
      {!noIndex && slug && (
        <Helmet>
          <link rel="canonical" href={hostMode ? `${window.location.origin}/` : `${window.location.origin}/${slug}`} />
        </Helmet>
      )}
      {effectiveStatusPage.google_analytics_code && (
        <GoogleAnalytics
          code={effectiveStatusPage.google_analytics_code}
          isV4={effectiveStatusPage.has_v4_google_analytics_code}
        />
      )}
      <ThemeRenderer
        statusPage={effectiveStatusPage}
        slug={slug ?? ""}
        mode={mode}
        pageId={parsedId}
        isDark={isDark}
        onToggleDarkMode={handleToggleDarkMode}
        activeTab={activeTab as StatusPageTab}
        onTabChange={(tab) => changeOuterTab(tab)}
        currentStatusTab={currentStatusTab as CurrentStatusTabType}
        onStatusTabChange={(tab) => changeStatusTab(tab)}
        historyTab={historyTab as HistoryTabType}
        onHistoryTabChange={(tab) => changeHistoryTab(tab)}
        historyMounted={historyMounted}
        historyResult={historyResult}
        allIncidents={allIncidents}
        statusDateRange={statusDateRange}
        onRefresh={handleRefresh}
        autoRefreshPageKey={autoRefreshPageKey}
        componentStatuses={componentStatuses}
        metrics={effectiveStatusPage.metrics}
        componentLinkTo={componentLinkTo}
      />
    </>
  );
};

export default StatusPageConsumption;
